/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface RegionConstraint {
  districts: string[];
  schools: string[];
}

export const getRegionConstraints = (regionId: string, regionName: string): RegionConstraint => {
  const normId = (regionId || '').toLowerCase();
  
  if (normId === 'training') {
    return {
      districts: ['Training School District (training)'],
      schools: ['Bayside High School (training)', 'Valley View High School (training)']
    };
  }
  
  if (normId === 'charleston') {
    return {
      districts: ['Charleston County School District (charleston)'],
      schools: [
        'Charleston Charter School for Math and Science (charleston)',
        'Wando High School (charleston)',
        'Academic Magnet High School (charleston)',
        'West Ashley High School (charleston)'
      ]
    };
  }
  
  if (normId === 'greenville') {
    return {
      districts: ['Greenville County School District (greenville)'],
      schools: [
        'Greenville High School (greenville)',
        'Wade Hampton High School (greenville)',
        'J.L. Mann High School (greenville)',
        'Eastside High School (greenville)',
        'Riverside High School (greenville)'
      ]
    };
  }

  if (normId === 'spartanburg') {
    return {
      districts: [
        'Spartanburg School District 1 (spartanburg)',
        'Spartanburg School District 2 (spartanburg)',
        'Spartanburg School District 3 (spartanburg)',
        'Spartanburg School District 4 (spartanburg)',
        'Spartanburg School District 5 (spartanburg)',
        'Spartanburg School District 6 (spartanburg)',
        'Spartanburg School District 7 (spartanburg)'
      ],
      schools: [
        'Campobello Gramling School (spartanburg)',
        'Chapman High School (spartanburg)',
        'Landrum High School (spartanburg)',
        'Boiling Springs High School (spartanburg)',
        'Chesnee High School (spartanburg)',
        'Broome High School (spartanburg)',
        'Woodruff High School (spartanburg)',
        'James F. Byrnes High School (spartanburg)',
        'Dorman High School (spartanburg)',
        'Spartanburg High School (spartanburg)',
        'Daniel Morgan Technology Center (spartanburg)',
        'Swofford Career Center (spartanburg)',
        'R.D. Anderson Applied Technology Center (spartanburg)',
        'Whitlock Flexible Learning Center (spartanburg)'
      ]
    };
  }

  if (normId === 'richland') {
    return {
      districts: ['Richland School District One (richland)', 'Richland School District Two (richland)'],
      schools: [
        'Columbia High School (richland)',
        'Spring Valley High School (richland)',
        'Ridge View High School (richland)',
        'Dreher High School (richland)'
      ]
    };
  }

  if (normId === 'horry') {
    return {
      districts: ['Horry County Schools (horry)'],
      schools: [
        'Myrtle Beach High School (horry)',
        'Carolina Forest High School (horry)',
        'Socastee High School (horry)',
        'Conway High School (horry)'
      ]
    };
  }

  return {
    districts: [],
    schools: []
  };
};

export const getRegionDarkBg = (regionId: string) => {
  const deepBgs: Record<string, string> = {
    'spartanburg': '#03142c', // Elegant Deep navy for Spartanburg County
    'union': '#0a1024',
    'charleston': '#021822',  // Ocean dark for Charleston County
    'greenville': '#042211',  // Forest deep dark for Greenville
    'richland': '#18022d',    // Deep violet for Richland
    'horry': '#261502',       // Deep sunset gold for Horry
    'lexington': '#240212',   // Deep wine for Lexington
    'beaufort': '#071626',
  };
  return deepBgs[(regionId || '').toLowerCase()] || '#091526'; // Standard deep slate/navy
};

export const getRegionThemeAccent = (regionId: string) => {
  const themes: Record<string, string> = {
    'spartanburg': '#b9e612',
    'charleston': '#38bdf8',
    'greenville': '#10b981',
    'richland': '#8b5cf6',
    'horry': '#f59e0b',
    'lexington': '#ec4899',
    'beaufort': '#38bdf8',
  };
  return themes[(regionId || '').toLowerCase()] || '#b9e612';
};

/**
 * Formats a region identifier or name into a properly capitalized proper noun.
 * Example: 'spartanburg' -> 'Spartanburg', 'spartanburg-county' -> 'Spartanburg County', 'global' -> 'South Carolina'
 */
export const formatRegionProperNoun = (regionIdOrName?: string | null): string => {
  if (!regionIdOrName || typeof regionIdOrName !== 'string') {
    return 'Spartanburg';
  }
  const trimmed = regionIdOrName.trim();
  if (!trimmed) return 'Spartanburg';

  const lower = trimmed.toLowerCase();
  if (lower === 'global' || lower === 'wbl portal' || lower === 'work-based learning portal') {
    return 'South Carolina';
  }

  // Split by hyphens, underscores, or spaces and capitalize each token
  const words = trimmed
    .replace(/[_-]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .map(word => {
      const wLower = word.toLowerCase();
      if (wLower === 'sc' || wLower === 'wbl') return wLower.toUpperCase();
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });

  return words.join(' ');
};

/**
 * Returns a region name explicitly followed by 'Region' capitalized as a proper noun.
 * Example: 'spartanburg' -> 'Spartanburg Region', 'Spartanburg County' -> 'Spartanburg County Region'
 */
export const formatRegionWithNoun = (regionIdOrName?: string | null): string => {
  const proper = formatRegionProperNoun(regionIdOrName);
  if (proper.toLowerCase().endsWith('region')) {
    // Ensure 'Region' is capitalized
    return proper.replace(/region$/i, 'Region');
  }
  return `${proper} Region`;
};

